import React, { useRef, useState, useEffect, useMemo } from 'react';
import { Play, Pause, Lock } from 'lucide-react';
import { fileUrl } from '../api';

// Seeded pseudo-random waveform so bars are stable per id
function makeBars(seed, count = 48) {
  let s = 0;
  for (let i = 0; i < seed.length; i++) s = (s * 31 + seed.charCodeAt(i)) % 100000;
  const rng = () => { s = (s * 1103515245 + 12345) % 2147483648; return s / 2147483648; };
  return Array.from({ length: count }, (_, i) => {
    const env = Math.sin((i / count) * Math.PI);
    return 0.25 + (0.35 + rng() * 0.65) * (0.5 + env * 0.5);
  });
}

export default function AudioPlayer({ id, src, previewSeconds = null, locked = false, lockLabel = 'FULL SONG AFTER PURCHASE', previewLabel, compact = false, onPlay }) {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0); // 0..1
  const [dur, setDur] = useState(0);
  const bars = useMemo(() => makeBars(id || src || 'x', compact ? 40 : 52), [id, src, compact]);

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    const onTime = () => {
      const cap = previewSeconds && locked ? previewSeconds : (a.duration || 0);
      if (previewSeconds && locked && a.currentTime >= previewSeconds) {
        a.pause(); a.currentTime = 0; setPlaying(false); setProgress(0); return;
      }
      setProgress(cap ? Math.min(1, a.currentTime / cap) : 0);
    };
    const onMeta = () => setDur(a.duration || 0);
    const onEnd = () => { setPlaying(false); setProgress(0); };
    a.addEventListener('timeupdate', onTime);
    a.addEventListener('loadedmetadata', onMeta);
    a.addEventListener('ended', onEnd);
    return () => { a.removeEventListener('timeupdate', onTime); a.removeEventListener('loadedmetadata', onMeta); a.removeEventListener('ended', onEnd); };
  }, [previewSeconds, locked]);

  const toggle = () => {
    const a = audioRef.current;
    if (!a) return;
    if (playing) { a.pause(); setPlaying(false); }
    else {
      document.querySelectorAll('audio').forEach((el) => { if (el !== a) el.pause(); });
      a.play().then(() => { setPlaying(true); onPlay && onPlay(); }).catch(() => {});
    }
  };

  const fmt = (t) => { if (!t || isNaN(t)) return '0:00'; const m = Math.floor(t / 60); const s = Math.floor(t % 60); return `${m}:${s.toString().padStart(2, '0')}`; };
  const label = previewLabel || (previewSeconds && locked ? `${fmt(previewSeconds)} prev.` : fmt(dur));

  return (
    <div className="flex items-center gap-4 w-full">
      <audio ref={audioRef} src={fileUrl(src)} preload="none" />
      <button onClick={toggle} aria-label={playing ? 'Pause' : 'Play'}
        className="shrink-0 grid place-items-center rounded-full btn-gold"
        style={{ width: compact ? 40 : 48, height: compact ? 40 : 48 }}>
        {playing ? <Pause size={compact ? 16 : 18} /> : <Play size={compact ? 16 : 18} className="ml-0.5" />}
      </button>
      <div className="flex items-end gap-[2px] h-10 flex-1 min-w-0 overflow-hidden">
        {bars.map((b, i) => {
          const active = i / bars.length <= progress;
          return (
            <span key={`${id}-bar-${i}`} className="flex-1 rounded-full"
              style={{ height: `${b * 100}%`, minWidth: 2,
                background: active ? 'linear-gradient(180deg,#F3D98A,#E0B755)' : 'rgba(224,183,85,0.18)',
                transition: 'background .1s' }} />
          );
        })}
      </div>
      {locked && (
        <span className="hidden sm:flex items-center gap-1 text-[10px] tracking-widest text-gold/70 font-mono uppercase shrink-0">
          <Lock size={11} /> {lockLabel}
        </span>
      )}
      <span className="text-xs text-cream/50 font-mono shrink-0 w-16 text-right">{label}</span>
    </div>
  );
}
